import React, { JSX, MouseEventHandler } from 'react';
import ClinicalTrialsResult from './ClinicalTrialsResult';
import PaginationControls from '../pld/shared/SearchPaginationControls';
import { ClinicalTrialsResultsSkeleton } from './ClinicalTrialsSkeletons';
import { ClinicalTrial } from 'lib/clinical-trials/types';

type ClinicalTrialsSearchResultsProps = {
  apiError?: boolean;
  results?: ClinicalTrial[];
  page: number;
  pageSize: number;
  clearAllFilters: MouseEventHandler<HTMLButtonElement>;
  count: number;
  updatePage: (page: number) => void;
  loading?: boolean;
  getInterestUrl?: (nctId: string) => string;
  onLearnMore: (nctId: string) => void;
};

const ClinicalTrialsSearchResults = (
  props: ClinicalTrialsSearchResultsProps
): JSX.Element => {
  const {
    apiError,
    loading,
    clearAllFilters,
    count,
    page,
    results,
    pageSize,
    updatePage,
    getInterestUrl,
    onLearnMore,
  } = props;

  return (
    <div className={'results-list-container ' + (loading ? 'loading' : '')}>
      {loading ? (
        <ClinicalTrialsResultsSkeleton />
      ) : results?.length === 0 ? (
        <div className="no-results-message" suppressHydrationWarning>
          {apiError ? (
            <>
              <h2>
                Something&rsquo;s not quite right. Let&rsquo;s help you find
                what you&rsquo;re looking for.
              </h2>
              <p>You can try:</p>
              <ul>
                <li>
                  <button className="osf-link" onClick={clearAllFilters}>
                    Resetting your filters
                  </button>
                </li>
                <li>
                  <a
                    href={typeof location !== 'undefined' ? location.href : '#'}
                  >
                    Refreshing the page
                  </a>
                </li>
                <li>
                  <a href="https://www.osfhealthcare.org">
                    Returning to the OSF HealthCare homepage
                  </a>
                </li>
              </ul>
            </>
          ) : (
            <>
              <h2>We can&rsquo;t find any matching clinical trials.</h2>
              <p>Here are a few tips that might help:</p>
              <ul>
                <li>Check your spelling.</li>
                <li>
                  Be less specific — try removing a filter or choosing a
                  different keyword.
                </li>
                <li>
                  Want to start over?{' '}
                  <button className="osf-link" onClick={clearAllFilters}>
                    Reset your filters.
                  </button>
                </li>
              </ul>
            </>
          )}
        </div>
      ) : (
        <>
          <ul className="results-list clinical-trials-results-list">
            {results?.map((trial, index) => (
              <li className="results-list-item" key={`${trial.nctId}-${index}`}>
                <ClinicalTrialsResult
                  trial={trial}
                  getInterestUrl={getInterestUrl}
                  onLearnMore={onLearnMore}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {!loading || count > pageSize ? (
        <PaginationControls
          count={count}
          pageSize={pageSize}
          page={page}
          updatePage={updatePage}
        />
      ) : null}
    </div>
  );
};

export default ClinicalTrialsSearchResults;
